/**
 * MarketSwitcher — compact pill in the navbar that lets visitors flip
 * between IE, UK and US.
 *
 * Fix (iter65): previously the pill fell back to IE's flag whenever the
 * resolved market was one not in the local MARKETS array (e.g. US), which
 * caused an "IE flag / US body copy" mismatch on /us/agent-match. Now the
 * flag ALWAYS reflects the actual market from useMarket(), and only the
 * clickable options in the popover are gated by feature flags.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useMarket } from '../hooks/useMarket';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from './ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MARKETS = [
  { code: 'IE', label: 'Ireland',        flag: '🇮🇪', prefix: '/'    },
  { code: 'UK', label: 'United Kingdom', flag: '🇬🇧', prefix: '/uk'  },
  { code: 'US', label: 'United States',  flag: '🇺🇸', prefix: '/us'  },
];

// Map useMarket().code (which uses 'GB') to the switcher's canonical form.
const canonical = (m) => (m === 'GB' ? 'UK' : m);

const apply = (code) => {
  try {
    localStorage.setItem('geo_override', code === 'UK' ? 'GB' : code);
    localStorage.removeItem('geo_bucket_v1');
  } catch { /* private mode */ }
  const next = MARKETS.find((m) => m.code === code);
  window.location.assign(next?.prefix || '/');
};

export const MarketSwitcher = ({ transparent = false }) => {
  const market = useMarket();
  const [flags, setFlags] = useState({ uk: false, us: false });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/admin/feature-flags`)
      .then((r) => {
        if (!cancelled) {
          setFlags({
            uk: !!r.data?.uk_market_enabled,
            us: !!r.data?.us_market_enabled,
          });
        }
      })
      .catch(() => { if (!cancelled) setFlags({ uk: false, us: false }); });
    return () => { cancelled = true; };
  }, []);

  const currentCode = canonical(market.code) || 'IE';
  // Always render a valid current entry, even for markets not (yet) clickable
  // — this is the core of the iter65 fix.
  const current = MARKETS.find((m) => m.code === currentCode) || MARKETS[0];

  // Clickable options = whatever markets are live behind the flag.
  const options = MARKETS.filter((m) => m.code === 'IE'
    || (m.code === 'UK' && flags.uk)
    || (m.code === 'US' && flags.us));

  // If only one market is live and the current market matches it, hide the pill
  // (nothing to switch to). If the current market is one that's NOT clickable,
  // we still show the pill so at least the flag is honest.
  const clickableAlternatives = options.filter((m) => m.code !== currentCode);
  if (clickableAlternatives.length === 0 && options.length <= 1) return null;

  const triggerClass = transparent
    ? 'text-white border-white/30 hover:bg-white/10'
    : 'text-gray-700 border-gray-200 hover:bg-gray-50';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="market-switcher-trigger"
          aria-label={`Switch market — currently ${current.label}`}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${triggerClass}`}
        >
          <span className="text-sm leading-none" aria-hidden>{current.flag}</span>
          <span className="hidden md:inline">{current.code}</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-56 p-1" data-testid="market-switcher-menu">
        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
          <Globe className="h-3 w-3" /> Choose your market
        </div>
        {options.map((m) => {
          const active = m.code === currentCode;
          return (
            <button
              key={m.code}
              type="button"
              onClick={() => { setOpen(false); if (!active) apply(m.code); }}
              data-testid={`market-switcher-option-${m.code.toLowerCase()}`}
              className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm rounded-md text-left transition-colors ${
                active ? 'bg-[#F5EFFF] text-[#5b35a8]' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <span className="text-base leading-none" aria-hidden>{m.flag}</span>
                <span>{m.label}</span>
              </span>
              {active && <Check className="h-3.5 w-3.5 text-[#7042cc]" />}
            </button>
          );
        })}
        <div className="px-2 pt-1.5 pb-1 text-[10px] text-gray-400 border-t mt-1">
          Currency &amp; locale update instantly.
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MarketSwitcher;
